import { useEffect, useState } from 'react';
import axios from 'axios';
import Spinner from '../../components/Spinner';

const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const annoCorrente = new Date().getFullYear();

export default function IndiciIstat() {
  const [indici, setIndici] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ anno: annoCorrente, mese: new Date().getMonth() + 1, valore: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [importing, setImporting] = useState(false);
  const [msgImport, setMsgImport] = useState('');

  function carica() {
    axios.get('/api/indici-istat')
      .then(r => setIndici(r.data))
      .finally(() => setLoading(false));
  }

  useEffect(() => { carica(); }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      await axios.post('/api/indici-istat', form);
      setMsg('Indice salvato!');
      carica();
    } catch {
      setMsg('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }

  async function handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setMsgImport('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post('/api/indici-istat/importa', fd);
      setMsgImport(`Importati ${r.data.inseriti} indici.`);
      carica();
    } catch (err) {
      setMsgImport('Errore: ' + (err.response?.data?.error || err.message));
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  }

  async function elimina(id) {
    if (!window.confirm('Eliminare questo indice?')) return;
    await axios.delete(`/api/indici-istat/${id}`);
    carica();
  }

  async function svuota() {
    if (!window.confirm('Sei sicura di voler eliminare TUTTI gli indici ISTAT? L\'operazione non è reversibile.')) return;
    try {
      await axios.delete('/api/indici-istat/svuota');
      setMsg('Tabella svuotata.');
      carica();
    } catch (err) {
      setMsg('Errore: ' + (err.response?.data?.error || err.message));
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-[#1A3A5C] mb-6">Indici ISTAT</h1>
      <p className="text-sm text-gray-500 mb-6">
        Inserisci i valori dell'indice ISTAT dei costi di costruzione (base 100 = anno di riferimento).
        Il calcolo SOA utilizzerà automaticamente l'indice più recente inserito.
      </p>

      {/* Import da Excel */}
      <div className="bg-white rounded-xl shadow p-5 mb-6">
        <h2 className="font-semibold text-[#1A3A5C] mb-2">Importa da Excel</h2>
        <p className="text-xs text-gray-500 mb-3">
          Formato atteso: colonne <strong>id</strong> (ignorata), <strong>indice istat</strong>, <strong>data</strong> (es. gen-26, feb-26…).
          I dati esistenti vengono aggiornati.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <label className={`cursor-pointer bg-[#1A3A5C] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition ${importing ? 'opacity-50 pointer-events-none' : ''}`}>
            {importing ? 'Importo...' : 'Scegli file Excel'}
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} disabled={importing} />
          </label>
          <button
            type="button"
            onClick={svuota}
            className="border border-red-300 text-red-600 px-5 py-2 rounded-lg text-sm font-medium hover:bg-red-50 transition"
          >
            Svuota tabella
          </button>
          {msgImport && <span className={`text-sm ${msgImport.startsWith('Errore') ? 'text-red-600' : 'text-green-600'}`}>{msgImport}</span>}
        </div>
      </div>

      {/* Form inserimento */}
      <div className="bg-white rounded-xl shadow p-5 mb-6">
        <h2 className="font-semibold text-[#1A3A5C] mb-4">Aggiungi / aggiorna indice</h2>
        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Anno</label>
            <input
              type="number"
              min="2000"
              max="2100"
              value={form.anno}
              onChange={e => setForm(f => ({ ...f, anno: parseInt(e.target.value) }))}
              className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Mese</label>
            <select
              value={form.mese}
              onChange={e => setForm(f => ({ ...f, mese: parseInt(e.target.value) }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
            >
              {MESI.map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Valore indice</label>
            <input
              type="number"
              step="0.1"
              required
              placeholder="es. 108.5"
              value={form.valore}
              onChange={e => setForm(f => ({ ...f, valore: e.target.value }))}
              className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="bg-[#1A3A5C] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition disabled:opacity-50"
          >
            {saving ? 'Salvo...' : 'Salva'}
          </button>
          {msg && <span className={`text-sm ${msg.includes('Errore') ? 'text-red-600' : 'text-green-600'}`}>{msg}</span>}
        </form>
      </div>

      {/* Tabella indici */}
      {loading ? <Spinner /> : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          {indici.length === 0 ? (
            <p className="text-center text-gray-500 py-10">Nessun indice inserito</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">Anno</th>
                  <th className="text-left px-4 py-3">Mese</th>
                  <th className="text-right px-4 py-3">Valore</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {indici.map((ind, i) => (
                  <tr key={ind.id} className={i === 0 ? 'bg-blue-50 font-medium' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2.5">{ind.anno}</td>
                    <td className="px-4 py-2.5">{MESI[ind.mese - 1]}</td>
                    <td className="px-4 py-2.5 text-right">{ind.valore}</td>
                    <td className="px-4 py-2.5 text-right">
                      {i === 0 && <span className="text-xs text-[#2E6096] font-semibold mr-3">← usato nei calcoli</span>}
                      <button onClick={() => elimina(ind.id)} className="text-red-500 hover:underline text-xs">Elimina</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
